var _react_refresh_temp_1;
import { jsx as _jsx } from "react/jsx-runtime";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { RouterProvider } from 'react-router-dom';
import '@lucidlink/uicore/design-system/theme';
import { setRootLogger } from '@lucidlink/tscore';
import { baseI18nConfig, buildComponentsTree, getClientData, I18nProvider, initUncaughtErrorHandling, initUncaughtUnhandledRejectionHandling, ModalProvider, ToastServiceProvider, UserClientProvider, } from '@lucidlink/uicore';
import { AccountRecoveryEmailProvider, AppUiProvider, AuthProvider, BroadcastChannelProvider, DependencyInjectionProvider, DrawerProvider, FeatureFlagProvider, initAmplitude, initSentry, LucidDevToolsProvider, LucidSdkProvider, } from '@lucidlink/workspacemanagement';
import { createDefaultQueryClientOptions } from '@lucidlink/workspacemanagement/query-hooks/query-client-config';
import errorsBG from '@lucidlink/workspacemanagement/translations/bg/errors.json';
import translationsBG from '@lucidlink/workspacemanagement/translations/bg/translation.json';
import errorsEN from '@lucidlink/workspacemanagement/translations/en/errors.json';
import translationsEN from '@lucidlink/workspacemanagement/translations/en/translation.json';
import { DownloadProvider, FilePreviewProvider } from '#context';
import { broadcastGuestLinkLogoutOnError } from '#features/guest-links';
import { createMobileRouter } from '#mobile';
import { envLog, ObservableRootLogger } from '#services';
import { createRouter } from './createRouter';
import { dependencies } from './dependencies';
import webTranslationsBG from './translations/bg/translation.json';
import webTranslationsEN from './translations/en/translation.json';
envLog();
setRootLogger(ObservableRootLogger.getLogger());
// External Integrations
initSentry();
initAmplitude({ requiresCookieConsent: Boolean(process.env.ONE_TRUST_ID) });
initUncaughtErrorHandling((errorEvent) => {
    /**
     * Note: In development environment, React error boundaries will allow errors to bubble up,
     * so this error handler will catch all errors regardless.
     * https://react.dev/reference/react/Component#componentdidcatch-caveats
     *
     * The following is needed to simulate the production env behavior on development env:
     */
    if (errorEvent.error instanceof Error &&
        errorEvent.error.stack?.includes('invokeGuardedCallbackDev')) {
        return;
    }
    // eslint-disable-next-line no-console -- We log the error
    console.error('Uncaught error', errorEvent);
});
initUncaughtUnhandledRejectionHandling((errorEvent) => {
    // This will only handle cases when the promise is
    // rejected because of an Error being thrown
    if (errorEvent.reason instanceof Error) {
        // eslint-disable-next-line no-console -- We log the unhandled rejection
        console.error('Unhandled promise rejection', errorEvent.reason);
        return;
    }
    // eslint-disable-next-line no-console -- We log the unhandled rejection
    console.error('Unhandled promise rejection', errorEvent);
});
const queryClient = new QueryClient({
    defaultOptions: createDefaultQueryClientOptions(),
    mutationCache: new MutationCache({
        onError: broadcastGuestLinkLogoutOnError,
    }),
    queryCache: new QueryCache({ onError: broadcastGuestLinkLogoutOnError }),
});
// The 'errors.json' files are managed by us manually.
// They are not being extracted by the i18n library.
// They consist of key corresponding to a backend error code and a value - human readable message.
// TODO: Desktop-specific and Web-specific translations will be defined in DesktopClient and
// WebClient, respectively, and passed into the App (through the AppProps), which will merge them
// with the shared translations defined above.
const translationResources = {
    bg: {
        [baseI18nConfig.extract.defaultNS]: {
            ...translationsBG,
            ...webTranslationsBG,
            ...errorsBG,
        },
    },
    en: {
        [baseI18nConfig.extract.defaultNS]: {
            ...translationsEN,
            ...webTranslationsEN,
            ...errorsEN,
        },
    },
};
// Infrastructure providers: no API calls, no business logic, only dependencies without which
// the application cannot even start. They should not trigger any network requests or any other side effects.
const infraProvidersWithProps = [
    [DependencyInjectionProvider, { dependencies }],
    [UserClientProvider, {}],
    [BroadcastChannelProvider, {}],
    [QueryClientProvider, { client: queryClient }],
    [LucidSdkProvider, {}],
    [I18nProvider, { resources: translationResources }],
];
// App providers: those can trigger side effects and network requests, but can only work if Infra providers are working
// They depend directly on the infrastructure providers, without them, they cannot function separately.
const appProvidersWithProps = [
    [AuthProvider, {}],
    [AccountRecoveryEmailProvider, {}],
    [FeatureFlagProvider, {}],
    [ToastServiceProvider, {}],
    [AppUiProvider, {}],
    [DrawerProvider, {}],
    [ModalProvider, {}],
    [DownloadProvider, {}],
    [FilePreviewProvider, {}],
    [LucidDevToolsProvider, {}],
];
// Use only the necessary ones on mobile:
const mobileProvidersWithProps = [
    [DependencyInjectionProvider, { dependencies }],
    [UserClientProvider, {}],
    [BroadcastChannelProvider, {}],
    [QueryClientProvider, { client: queryClient }],
    [LucidSdkProvider, {}],
    [AuthProvider, {}],
    [I18nProvider, { resources: translationResources }],
    [FeatureFlagProvider, {}],
    [ToastServiceProvider, {}],
];
const infraProviders = buildComponentsTree(infraProvidersWithProps);
const appProviders = buildComponentsTree(appProvidersWithProps);
const mobileProviders = buildComponentsTree(mobileProvidersWithProps);
export const App = () => {
    const { isMobile } = getClientData();
    /**
     * Check the README file for details
     * on splitting the web and the web-mobile
     * routers.
     */
    const router = isMobile
        ? createMobileRouter(mobileProviders)
        : createRouter(infraProviders, appProviders);
    return (_jsx(StrictMode, { children: _jsx(RouterProvider, { router: router }) }));
};
_react_refresh_temp_1 = App;
$RefreshReg$(_react_refresh_temp_1, "App");
